// FLAG:useDocuAi — the "needs review" marker for the review's HEADER fields (receipt date,
// invoice number, delivery date), rendered inside the field's own label.
//
// WHY this exists: the marker used to live only in review-payment-fields.tsx, i.e. only on the
// „Bezahlung" card. `getFieldValidation` had exactly ONE caller in the whole client, so every
// verdict DocuAI stamped on a header field was carried all the way into omxweb and then never
// shown. Meanwhile the receipt chat described those very fields as requiring review
// (chat-situation's uncertainFields) — so the reviewer was sent looking for a mark that was
// never painted, and answered „QUATSCH" (DMS issue a7285ab1 / casebook 0085).
//
// Deliberately IN THE LABEL rather than as a block under the field: that is where the payment
// card puts it (same visual language), and it leaves the form's flex rows untouched — the
// header fields size themselves with `flex: 1 0 30%`, so an extra child in the row would
// re-flow the whole line.
import React from 'react'
import clsx from 'clsx'
import { makeStyles } from '@mui/styles'

import lang from '@/lang'
import ThemeInterface from '@/theme-interface'
import { FieldTreatment } from '@/modules/document/components/review/field-validation'

export interface FieldCheck {
  treatment: FieldTreatment
  note?: string | null
}

/**
 * FLAG:useDocuAi — the date fields' verdict, including the case that has NO verdict.
 *
 * Missing analysis dates remain empty and require manual input before saving.
 * Mark the absence even when the analyser supplied no field verdict.
 *
 * An actual verdict always wins: DocuAI says the same thing with `fieldMeta.date` =
 * error/'required_missing' and says it in its own words, which are more specific than ours.
 * Outside the review (`inReview` false) nothing is ever added — a plain expense form has no
 * analysis to contradict, and its today-default is a legitimate default rather than a claim
 * about a document.
 */
export const resolveDateFieldCheck = (
  check: FieldCheck,
  { inReview, analysisDate }: { inReview: boolean, analysisDate?: string | null },
): FieldCheck => {
  if (check.treatment) return check
  if (!inReview || analysisDate) return check
  return { treatment: 'warning', note: lang.documents.reviewFieldCheck.dateNotDetermined }
}

const useStyles = makeStyles((theme: ThemeInterface) => ({
  // 'error' is red, 'warning' the yellow review color — the same mapping review-payment-fields
  // uses, so one review never shows the same verdict in two colours.
  errorLabel: {
    color: theme.red,
    fontWeight: 600,
  },
  warningLabel: {
    color: theme.yellow,
    fontWeight: 600,
  },
  hint: {
    marginLeft: 4,
    fontWeight: 400,
    fontSize: theme.fontSizes?.tiny ?? 10,
    cursor: 'help',
  },
  errorHint: {
    color: theme.red,
  },
  warningHint: {
    color: theme.yellow,
  },
}))

export interface ReviewFieldLabelProps {
  /** the fieldMeta/extraction key — only used to build a stable test id */
  field: string
  /** the label as the form renders it today, asterisk and all */
  text: string
  /** what the field's verdict should paint; null renders the plain label */
  treatment: FieldTreatment
  /** WHY it is marked — DocuAI's own validationNote when it has one */
  note?: string | null
}

/**
 * The field's label, marked when a check flagged it. With `treatment === null` this returns the
 * bare string, so a field without a verdict — and every surface that has no analysis at all —
 * renders exactly the DOM it rendered before this component existed.
 */
export const ReviewFieldLabel = ({ field, text, treatment, note }: ReviewFieldLabelProps) => {
  const classes = useStyles()
  if (!treatment) return <>{text}</>

  return (
    <span className={clsx(
      treatment === 'error' && classes.errorLabel,
      treatment === 'warning' && classes.warningLabel,
    )}>
      {text}
      <span
        className={clsx(classes.hint, treatment === 'error' ? classes.errorHint : classes.warningHint)}
        data-testid={`review-field-check-${field}`}
        // Which grade is shown, without depending on the generated JSS class name.
        data-state={treatment}
        title={note || lang.documents.reviewFieldCheck.lowConfidence}
      >
        {lang.documents.reviewFieldCheck.checkHint}
      </span>
    </span>
  )
}

export default ReviewFieldLabel
